import React, { Component } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button } from 'react-bootstrap';

// Error Boundary Class Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    console.log('🛑 Error Boundary: getDerivedStateFromError triggered');
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.log('🛑 Error Boundary: componentDidCatch triggered');
    console.error('❌ Error caught:', error);
    console.error('📍 Error info:', errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  resetError = () => {
    console.log('🔄 Resetting error boundary');
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-danger">
          <Card.Header className="bg-danger text-white">
            <strong>⚠️ Error Caught!</strong>
          </Card.Header>
          <Card.Body>
            <Alert variant="danger">
              <h5>Something went wrong!</h5>
              <p><strong>Error:</strong> {this.state.error && this.state.error.toString()}</p>
              <details style={{ whiteSpace: 'pre-wrap' }}>
                <summary>Click for error details</summary>
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </details>
            </Alert>
            <Button onClick={this.resetError} variant="primary">
              Try Again
            </Button>
          </Card.Body>
        </Card>
      );
    }

    return this.props.children;
  }
}

// Component that throws an error
const BuggyComponent = ({ shouldThrow }) => {
  console.log('🐛 Buggy Component: Rendering, shouldThrow =', shouldThrow);
  
  if (shouldThrow) {
    console.log('💥 Buggy Component: Throwing error!');
    throw new Error('💥 This is an intentional error!');
  }

  return (
    <Alert variant="success">
      <strong>✅ Component is working fine!</strong>
      <p className="mb-0">No errors here.</p>
    </Alert>
  );
};

// Safe Component (won't throw errors)
const SafeComponent = () => {
  console.log('✅ Safe Component: Rendering');
  return (
    <Alert variant="info">
      <strong>Safe Component</strong>
      <p className="mb-0">This component is outside the error boundary and will always render.</p>
    </Alert>
  );
};

const Lesson18_ErrorBoundary = () => {
  const [throwError, setThrowError] = React.useState(false);
  const [key, setKey] = React.useState(0);

  React.useEffect(() => {
    console.log('📖 Lesson 18: Error Boundaries - Loaded');
  }, []);

  const triggerError = () => {
    console.log('');
    console.log('━━━━━━━━ USER ACTION: Triggering Error ━━━━━━━━');
    setThrowError(true);
  };

  const reset = () => {
    console.log('');
    console.log('━━━━━━━━ USER ACTION: Resetting Component ━━━━━━━━');
    setThrowError(false);
    setKey(prev => prev + 1); // Force remount
  };

  return (
    <LessonLayout 
      lessonNumber={18}
      title="Error Boundaries" 
      description="Gracefully handle errors in React components"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>Error Boundary concept and implementation</li>
          <li>Catching errors in component tree</li>
          <li>Fallback UI for errors</li>
          <li>Error logging and reporting</li>
          <li>Limitations of Error Boundaries</li>
        </ul>
      </Alert>

      <SafeComponent />

      <Card className="mb-3">
        <Card.Header>Interactive Demo</Card.Header>
        <Card.Body>
          <div className="d-flex gap-2 mb-3">
            <Button onClick={triggerError} variant="danger">
              Trigger Error
            </Button>
            <Button onClick={reset} variant="secondary">
              Reset
            </Button>
          </div>

          <ErrorBoundary key={key}>
            <Card className="border-primary">
              <Card.Header className="bg-primary text-white">
                Component Wrapped in Error Boundary
              </Card.Header>
              <Card.Body>
                <BuggyComponent shouldThrow={throwError} />
              </Card.Body>
            </Card>
          </ErrorBoundary>
        </Card.Body>
      </Card>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Error Boundary Code</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error:', error);
    console.error('Info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}`}
              </pre>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Usage Example</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`// Wrap components with error boundary
<ErrorBoundary>
  <MyComponent />
</ErrorBoundary>

// Multiple boundaries for isolation
<ErrorBoundary>
  <Sidebar />
</ErrorBoundary>

<ErrorBoundary>
  <MainContent />
</ErrorBoundary>`}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h4 className="mt-4">⚠️ What Error Boundaries Catch</h4>
      <Row>
        <Col md={6}>
          <Alert variant="success">
            <strong>✅ Catches:</strong>
            <ul className="mb-0">
              <li>Errors in render methods</li>
              <li>Errors in lifecycle methods</li>
              <li>Errors in constructors</li>
              <li>Errors in child component tree</li>
            </ul>
          </Alert>
        </Col>
        <Col md={6}>
          <Alert variant="danger">
            <strong>❌ Does NOT Catch:</strong>
            <ul className="mb-0">
              <li>Event handlers (use try-catch)</li>
              <li>Asynchronous code (setTimeout, promises)</li>
              <li>Server-side rendering errors</li>
              <li>Errors in error boundary itself</li>
            </ul>
          </Alert>
        </Col>
      </Row>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Place error boundaries strategically (top-level, around widgets, etc.)</li>
          <li>Log errors to error reporting services (Sentry, LogRocket, etc.)</li>
          <li>Provide user-friendly error messages</li>
          <li>Offer a way to recover (reset button)</li>
          <li>Error boundaries must be class components (no hook equivalent yet)</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson18_ErrorBoundary;
